"use client"

import { useState, useEffect, useCallback } from "react"
import { propertyService } from "@/services/api"
import { transformApiProperties, transformApiPropertyDetail } from "@/utils/transformers"
import type { Property, PropertyQueryParams, PropertyDetail } from "@/types/api"

interface UsePropertiesOptions {
  initialParams?: PropertyQueryParams
  autoFetch?: boolean
}

interface UsePropertiesReturn {
  properties: Property[]
  loading: boolean
  error: string | null
  totalCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  currentPage: number
  totalPages: number
  fetchProperties: (params?: PropertyQueryParams) => Promise<void>
  refetch: () => Promise<void>
  clearError: () => void
}

export function useProperties(options: UsePropertiesOptions = {}): UsePropertiesReturn {
  const { initialParams = {}, autoFetch = true } = options

  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [hasPreviousPage, setHasPreviousPage] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [lastParams, setLastParams] = useState<PropertyQueryParams>(initialParams)

  const fetchProperties = useCallback(async (params: PropertyQueryParams = {}) => {
    setLoading(true)
    setError(null)

    try {
      const mergedParams = { ...params }
      const response = await propertyService.getProperties(mergedParams)

      const transformedProperties = transformApiProperties(response.items)

      setProperties(transformedProperties)
      setTotalCount(response.totalResults)
      setHasNextPage(response.pageIndex < response.pages)
      setHasPreviousPage(response.pageIndex > 1)
      setCurrentPage(response.pageIndex)
      setTotalPages(response.pages)
      setLastParams(mergedParams)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error al cargar las propiedades"
      setError(errorMessage)
      console.error("Error fetching properties:", err)
    } finally {
      setLoading(false)
    }
  }, [])

  const refetch = useCallback(() => {
    return fetchProperties(lastParams)
  }, [fetchProperties, lastParams])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchProperties(initialParams)
    }
  }, [autoFetch])

  return {
    properties,
    loading,
    error,
    totalCount,
    hasNextPage,
    hasPreviousPage,
    currentPage,
    totalPages,
    fetchProperties,
    refetch,
    clearError,
  }
}

// Specialized hook for featured properties
export function useFeaturedProperties(pageSize = 4) {
  const [featuredProperties, setFeaturedProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchFeaturedProperties = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await propertyService.getFeaturedProperties(pageSize)
      const transformedProperties = transformApiProperties(response.items)
      setFeaturedProperties(transformedProperties)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error al cargar las propiedades destacadas"
      setError(errorMessage)
      console.error("Error fetching featured properties:", err)
    } finally {
      setLoading(false)
    }
  }, [pageSize])

  useEffect(() => {
    fetchFeaturedProperties()
  }, [fetchFeaturedProperties])

  return {
    properties: featuredProperties,
    loading,
    error,
    refetch: fetchFeaturedProperties,
    clearError: () => setError(null),
  }
}

// Specialized hook for single property - FIXED
export function useProperty(id: number | null | undefined) {
  const [property, setProperty] = useState<PropertyDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchProperty = useCallback(async () => {
    // Don't fetch if no valid ID
    if (!id || id <= 0) {
      setProperty(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // console.log("Fetching property with ID:", id)
      const apiProperty = await propertyService.getPropertyById(id)
      // console.log("API response:", apiProperty)
      const transformedProperty = transformApiPropertyDetail(apiProperty)
      // console.log("Transformed property:", transformedProperty)
      setProperty(transformedProperty)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error al cargar la propiedad"
      setError(errorMessage)
      console.error("Error fetching property:", err)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchProperty()
  }, [fetchProperty])

  return {
    property,
    loading,
    error,
    refetch: fetchProperty,
    clearError: () => setError(null),
  }
}
